/**
 * Particle effects: coin showers, sparkles, glow pulses via PixiJS v8.
 * Lightweight custom particle system using Sprites + simple physics (no external library).
 */

import {
  Container,
  Sprite,
  Texture,
  Graphics,
  BlurFilter,
  Ticker,
} from 'pixi.js';
import { Logger } from '../utils/Logger';

const logger = new Logger('ParticleEffects');

/* ---------- helpers ---------- */

/** Simple lerp */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Random float in [min, max) */
function randRange(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

/* ---------- internal types ---------- */

interface CoinParticle {
  sprite: Sprite;
  vx: number;
  vy: number;
  rotSpeed: number;
  /** Elapsed time in seconds */
  age: number;
  /** Animated-texture frame index (fractional) */
  frameIdx: number;
  frameSpeed: number;
}

interface SparkleParticle {
  gfx: Graphics;
  vx: number;
  vy: number;
  age: number;
  lifetime: number;
  startScale: number;
}

/* ---------- coin texture cache ---------- */

const COIN_FRAME_COUNT = 20;
let _coinTexturesLoaded = false;
const _coinTextures: Texture[] = [];

function ensureCoinTextures(): Texture[] {
  if (_coinTexturesLoaded) return _coinTextures;
  _coinTexturesLoaded = true;

  for (let i = 0; i < COIN_FRAME_COUNT; i++) {
    const idx = String(i).padStart(5, '0');
    const path = `assets/effects/coin_particle_${idx}.png`;
    try {
      const tex = Texture.from(path);
      _coinTextures.push(tex);
    } catch {
      // asset not preloaded — fall through to fallback below
    }
  }

  // Fallback: generate a golden circle texture via Graphics if no image assets found
  if (_coinTextures.length === 0) {
    logger.warn('Coin textures not found, generating fallback golden circle');
    // Use Texture.WHITE as fallback — the sprite tint will make it golden
    _coinTextures.push(Texture.WHITE);
  }

  logger.info(`Coin textures ready: ${_coinTextures.length} frame(s)`);
  return _coinTextures;
}

/* ---------- sparkle graphics helper ---------- */

/** Draw a small 4-point star / diamond into a Graphics object */
function drawSparkle(g: Graphics, size: number, color: number): void {
  g.clear();
  const inner = size * 0.3;
  const points = 4;
  const step = Math.PI / points;
  const verts: number[] = [];

  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? size : inner;
    const angle = i * step - Math.PI / 2;
    verts.push(Math.cos(angle) * r, Math.sin(angle) * r);
  }

  g.poly(verts);
  g.fill({ color });
}

/* ========================================================================== */

/** Pre-allocation pool sizes */
const COIN_POOL_PREALLOC = 60;
const SPARKLE_POOL_PREALLOC = 20;

/** Lightweight particle system for visual effects */
export class ParticleEffects {
  /* ---- sprite pools ---- */
  private _coinPool: Sprite[] = [];
  private _sparklePool: Graphics[] = [];

  /* ---- active state ---- */
  private _activeCoinParticles: CoinParticle[] = [];
  private _activeSparkleParticles: SparkleParticle[] = [];
  private _coinContainer: Container | null = null;
  private _glowCleanups: Array<() => void> = [];
  private _tickerBound = false;
  private _stopped = false;
  private _initialized = false;

  /**
   * Pre-allocate sprite and graphics pools to avoid GC pressure during gameplay.
   * Call once after textures are loaded.
   */
  preallocatePools(): void {
    if (this._initialized) return;
    this._initialized = true;

    const textures = ensureCoinTextures();

    // Pre-allocate coin sprites
    for (let i = 0; i < COIN_POOL_PREALLOC; i++) {
      const s = new Sprite(textures[0]);
      s.anchor.set(0.5);
      s.visible = false;
      this._coinPool.push(s);
    }

    // Pre-allocate sparkle graphics
    for (let i = 0; i < SPARKLE_POOL_PREALLOC; i++) {
      const g = new Graphics();
      g.visible = false;
      this._sparklePool.push(g);
    }

    logger.info(`Pools pre-allocated: ${COIN_POOL_PREALLOC} coins, ${SPARKLE_POOL_PREALLOC} sparkles`);
  }

  /* ---- pool management ---- */

  private _getCoinSprite(): Sprite {
    const textures = ensureCoinTextures();
    if (this._coinPool.length > 0) {
      const s = this._coinPool.pop()!;
      s.visible = true;
      s.alpha = 1;
      s.scale.set(1);
      s.rotation = 0;
      return s;
    }
    const s = new Sprite(textures[0]);
    s.anchor.set(0.5);
    return s;
  }

  private _returnCoinSprite(s: Sprite): void {
    s.visible = false;
    this._coinPool.push(s);
  }

  private _getSparkleGfx(): Graphics {
    if (this._sparklePool.length > 0) {
      const g = this._sparklePool.pop()!;
      g.visible = true;
      g.alpha = 1;
      g.scale.set(1);
      return g;
    }
    return new Graphics();
  }

  private _returnSparkleGfx(g: Graphics): void {
    g.visible = false;
    this._sparklePool.push(g);
  }

  /* ---- ensure ticker ---- */

  private _ensureTicker(): void {
    if (this._tickerBound) return;
    this._tickerBound = true;
    Ticker.shared.add(this._onTick, this);
  }

  /* ---- main tick ---- */

  private _onTick = (): void => {
    const dt = Ticker.shared.deltaMS / 1000; // seconds

    this._updateCoins(dt);
    this._updateSparkles(dt);
  };

  /* ==================================================================== */
  /*  COIN SHOWER                                                          */
  /* ==================================================================== */

  /**
   * Spawn 50-100 coin sprites from the top of screen, falling with gravity.
   * Returns a promise that resolves when the shower finishes (~3 s).
   */
  coinShower(container: Container, designW: number, designH: number): Promise<void> {
    this._stopped = false;
    this._ensureTicker();

    const textures = ensureCoinTextures();
    const count = Math.floor(randRange(50, 101));

    // Dedicated container so we can clean up easily
    const coinLayer = new Container();
    coinLayer.zIndex = 1500;
    container.addChild(coinLayer);
    this._coinContainer = coinLayer;

    logger.info(`Coin shower: spawning ${count} coins`);

    for (let i = 0; i < count; i++) {
      const sprite = this._getCoinSprite();
      sprite.texture = textures[0];
      // Scale coins to a reasonable size
      sprite.scale.set(randRange(0.35, 0.65));

      // Start position: spread across top, slightly above screen
      sprite.x = randRange(0, designW);
      sprite.y = randRange(-80, -10);

      // Stagger spawn time via negative age
      const staggerDelay = randRange(0, 1.0);

      coinLayer.addChild(sprite);

      const particle: CoinParticle = {
        sprite,
        vx: randRange(-60, 60),
        vy: randRange(50, 200),
        rotSpeed: randRange(-4, 4),
        age: -staggerDelay, // negative = hasn't started yet
        frameIdx: randRange(0, textures.length),
        frameSpeed: randRange(12, 24), // frames per second
      };

      this._activeCoinParticles.push(particle);
    }

    // Resolve after ~3 seconds or when stopped
    const DURATION = 3.0;

    return new Promise<void>((resolve) => {
      const checkInterval = setInterval(() => {
        if (this._stopped || this._activeCoinParticles.length === 0) {
          clearInterval(checkInterval);
          this._finishCoinShower(container);
          resolve();
          return;
        }

        // Remove coins that have fallen below the screen
        const toRemove: CoinParticle[] = [];
        for (const p of this._activeCoinParticles) {
          if (p.age > DURATION + 1.0 || p.sprite.y > designH + 100) {
            toRemove.push(p);
          }
        }
        for (const p of toRemove) {
          const idx = this._activeCoinParticles.indexOf(p);
          if (idx >= 0) this._activeCoinParticles.splice(idx, 1);
          coinLayer.removeChild(p.sprite);
          this._returnCoinSprite(p.sprite);
        }
      }, 100);

      // Hard timeout
      setTimeout(() => {
        clearInterval(checkInterval);
        this._finishCoinShower(container);
        resolve();
      }, (DURATION + 1.5) * 1000);
    });
  }

  private _updateCoins(dt: number): void {
    const textures = ensureCoinTextures();
    const GRAVITY = 600; // px/s^2

    for (const p of this._activeCoinParticles) {
      p.age += dt;
      if (p.age < 0) {
        // Still waiting to spawn
        p.sprite.visible = false;
        continue;
      }
      p.sprite.visible = true;

      // Physics
      p.vy += GRAVITY * dt;
      p.sprite.x += p.vx * dt;
      p.sprite.y += p.vy * dt;
      p.sprite.rotation += p.rotSpeed * dt;

      // Animated texture (coin spin)
      if (textures.length > 1) {
        p.frameIdx += p.frameSpeed * dt;
        const frame = Math.floor(p.frameIdx) % textures.length;
        p.sprite.texture = textures[frame];
      }
    }
  }

  private _finishCoinShower(parentContainer: Container): void {
    // Return all remaining coin sprites to pool
    for (const p of this._activeCoinParticles) {
      if (p.sprite.parent) {
        p.sprite.parent.removeChild(p.sprite);
      }
      this._returnCoinSprite(p.sprite);
    }
    this._activeCoinParticles.length = 0;

    if (this._coinContainer) {
      parentContainer.removeChild(this._coinContainer);
      this._coinContainer.destroy({ children: false });
      this._coinContainer = null;
    }

    logger.info('Coin shower finished');
  }

  /* ==================================================================== */
  /*  SPARKLE EFFECT                                                       */
  /* ==================================================================== */

  /**
   * Spawn 5-10 sparkle particles around (x, y), expanding outward and fading.
   * Uses ADD blend mode for a glow look. Duration ~0.5 s.
   */
  sparkleAt(container: Container, x: number, y: number): void {
    this._stopped = false;
    this._ensureTicker();

    const count = Math.floor(randRange(5, 11));

    for (let i = 0; i < count; i++) {
      const gfx = this._getSparkleGfx();

      // Random color: white or gold
      const color = Math.random() > 0.5 ? 0xffffff : 0xffd700;
      const size = randRange(4, 10);
      drawSparkle(gfx, size, color);

      gfx.x = x;
      gfx.y = y;
      gfx.blendMode = 'add';

      const startScale = randRange(0.6, 1.2);
      gfx.scale.set(startScale);

      container.addChild(gfx);

      // Random direction outward
      const angle = randRange(0, Math.PI * 2);
      const speed = randRange(80, 200);

      const particle: SparkleParticle = {
        gfx,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        age: 0,
        lifetime: randRange(0.3, 0.6),
        startScale,
      };

      this._activeSparkleParticles.push(particle);
    }

    logger.debug(`Sparkle: ${count} particles at (${Math.round(x)}, ${Math.round(y)})`);
  }

  private _updateSparkles(dt: number): void {
    // In-place removal: iterate backwards to avoid allocation of toRemove array
    for (let i = this._activeSparkleParticles.length - 1; i >= 0; i--) {
      const p = this._activeSparkleParticles[i];
      p.age += dt;

      if (p.age >= p.lifetime || this._stopped) {
        // Remove by swap-with-last for O(1) removal
        this._activeSparkleParticles[i] = this._activeSparkleParticles[this._activeSparkleParticles.length - 1];
        this._activeSparkleParticles.pop();
        if (p.gfx.parent) {
          p.gfx.parent.removeChild(p.gfx);
        }
        this._returnSparkleGfx(p.gfx);
        continue;
      }

      const progress = p.age / p.lifetime;

      // Move outward
      p.gfx.x += p.vx * dt;
      p.gfx.y += p.vy * dt;

      // Fade out + scale down
      p.gfx.alpha = 1 - progress;
      p.gfx.scale.set(lerp(p.startScale, 0, progress));
    }
  }

  /* ==================================================================== */
  /*  GLOW PULSE                                                           */
  /* ==================================================================== */

  /**
   * Apply a pulsing blur + ADD glow to a target display object.
   * Resolves after `duration` ms.
   */
  glowPulse(target: Container, duration: number): Promise<void> {
    this._stopped = false;

    const blurFilter = new BlurFilter({
      strength: 0,
      quality: 2,
    });

    // Save previous filters and blend mode
    const prevFilters = target.filters ? [...target.filters] : [];
    const prevBlendMode = target.blendMode;

    target.filters = [...prevFilters, blurFilter];
    target.blendMode = 'add';

    const startTime = performance.now();

    let cancelled = false;

    const cleanup = () => {
      cancelled = true;
      // Restore original state
      target.filters = prevFilters.length > 0 ? prevFilters : null;
      target.blendMode = prevBlendMode;
      blurFilter.destroy();
    };

    this._glowCleanups.push(cleanup);

    return new Promise<void>((resolve) => {
      const onTick = () => {
        if (cancelled || this._stopped) {
          Ticker.shared.remove(onTick);
          if (!cancelled) cleanup();
          resolve();
          return;
        }

        const elapsed = performance.now() - startTime;
        if (elapsed >= duration) {
          Ticker.shared.remove(onTick);
          cleanup();
          resolve();
          return;
        }

        // Sine-wave pulse: 0 -> peak -> 0, twice per second
        const progress = elapsed / duration;
        const pulse = Math.sin(progress * Math.PI * 4) * 0.5 + 0.5; // 0..1
        blurFilter.strength = lerp(0, 8, pulse);
      };

      Ticker.shared.add(onTick);
    });
  }

  /* ==================================================================== */
  /*  STOP ALL                                                             */
  /* ==================================================================== */

  /** Immediately stop all running particle effects and clean up */
  stopAll(): void {
    logger.info('Stopping all particle effects');
    this._stopped = true;

    // Coins
    for (const p of this._activeCoinParticles) {
      if (p.sprite.parent) {
        p.sprite.parent.removeChild(p.sprite);
      }
      this._returnCoinSprite(p.sprite);
    }
    this._activeCoinParticles.length = 0;

    if (this._coinContainer) {
      if (this._coinContainer.parent) {
        this._coinContainer.parent.removeChild(this._coinContainer);
      }
      this._coinContainer.destroy({ children: false });
      this._coinContainer = null;
    }

    // Sparkles
    for (const p of this._activeSparkleParticles) {
      if (p.gfx.parent) {
        p.gfx.parent.removeChild(p.gfx);
      }
      this._returnSparkleGfx(p.gfx);
    }
    this._activeSparkleParticles.length = 0;

    // Glow cleanups
    for (const cleanup of this._glowCleanups) {
      cleanup();
    }
    this._glowCleanups.length = 0;
  }
}
